import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Card, Input, Progress, Space, Typography, message } from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  LinkOutlined,
  LockOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import AuthShell from "components/auth/AuthShell";
import AuthBrand from "components/auth/AuthBrand";
import paths from "routes/paths";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { Title, Text, Paragraph } = Typography;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LINKED_ACCOUNT_LOGIN_KEY = "ovacusLinkedAccountLogin";

const formStyle = `
  .ovacus-link-form .ant-input,
  .ovacus-link-form .ant-input-affix-wrapper {
    height: 54px;
    border-radius: 999px !important;
    border-color: ${OLIVEEB_COLORS.oliveebGreenSoft};
    background: ${OLIVEEB_COLORS.white} !important;
    font-size: 15px;
    font-weight: 700;
    box-shadow: 0 10px 22px rgba(16, 147, 87, 0.08);
  }
  .ovacus-link-form .ant-input-affix-wrapper-focused,
  .ovacus-link-form .ant-input-affix-wrapper:focus,
  .ovacus-link-form .ant-input:focus {
    border-color: ${OLIVEEB_COLORS.oliveebGreen} !important;
    box-shadow: 0 0 0 4px rgba(39, 174, 48, 0.12) !important;
  }
`;

const primaryButtonStyle = {
  height: 50,
  borderRadius: 999,
  fontWeight: 900,
  background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreen} 0%, ${OLIVEEB_COLORS.greenDark} 100%)`,
  borderColor: OLIVEEB_COLORS.oliveebGreen,
  boxShadow: "0 14px 28px rgba(16, 147, 87, 0.24)",
};

const secondaryButtonStyle = {
  height: 46,
  borderRadius: 999,
  fontWeight: 900,
  color: OLIVEEB_COLORS.oliveebBlue,
  borderColor: OLIVEEB_COLORS.oliveebBlue,
  background: OLIVEEB_COLORS.white,
};

const parseDisplayName = (name = "") => {
  const trimmed = String(name || "").trim();
  if (!trimmed) return { firstName: "", lastName: "" };

  const cleaned = trimmed.replace(/\s+Apple$/i, "").trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: parts[0] || "", lastName: "" };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

const validatePassword = (password) => {
  if (password.length < 8) return "Password must be at least 8 characters long";
  if (!/(?=.*[a-z])/.test(password)) return "Password must contain at least one lowercase letter";
  if (!/(?=.*[A-Z])/.test(password)) return "Password must contain at least one uppercase letter";
  if (!/(?=.*\d)/.test(password)) return "Password must contain at least one number";
  return null;
};

const getIdentityToken = (appleResponse = {}, identityToken = "") =>
  identityToken ||
  appleResponse?.authorization?.id_token ||
  appleResponse?.authorization?.idToken ||
  appleResponse?.identityToken ||
  appleResponse?.id_token ||
  appleResponse?.idToken ||
  "";

const getAppleUserId = (appleResponse = {}, fallback = "") =>
  fallback ||
  (typeof appleResponse?.user === "string" ? appleResponse.user : "") ||
  appleResponse?.userId ||
  "";

const providerLabel = (provider = "apple") => {
  const normalized = String(provider || "apple").trim().toLowerCase();
  if (normalized === "google") return "Google";
  if (normalized === "microsoft") return "Microsoft";
  if (normalized === "facebook") return "Facebook";
  if (normalized === "linkedin") return "LinkedIn";
  return "Apple";
};

const AuthAppleSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithApple, linkSocialAccount, linkAppleAccount, refreshAuth } = useAuth();

  const state = location.state || {};
  const provider = String(state.provider || "apple").trim().toLowerCase();
  const providerName = providerLabel(provider);
  const appleResponse = state.appleResponse || {};
  const authPayload = state.authPayload || {};
  const identityToken = getIdentityToken(appleResponse, state.identityToken);
  const appleUserId = getAppleUserId(appleResponse, state.appleUserId);
  const providerUserId = state.providerUserId || authPayload?.providerUserId || authPayload?.userId || appleUserId;
  const userInfo = state.userInfo || {};

  const initialDisplayName =
    userInfo.name ||
    appleResponse.name ||
    [appleResponse.firstName, appleResponse.lastName].filter(Boolean).join(" ") ||
    "";

  const parsedName = useMemo(() => parseDisplayName(initialDisplayName), [initialDisplayName]);

  const [mode, setMode] = useState(state.mode || (state.requiresLinkApproval ? "link" : "setup"));
  const [linkStep, setLinkStep] = useState("confirm");
  const [email, setEmail] = useState(userInfo.email || appleResponse.email || "");
  const [firstName, setFirstName] = useState(parsedName.firstName);
  const [lastName, setLastName] = useState(parsedName.lastName);
  const [phone, setPhone] = useState(appleResponse.phone || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [linkingProcessId, setLinkingProcessId] = useState(state.linkingProcessId || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(state.message || "");

  useEffect(() => {
    if (!email && userInfo.email) setEmail(userInfo.email);
    if (!firstName && parsedName.firstName) setFirstName(parsedName.firstName);
    if (!lastName && parsedName.lastName) setLastName(parsedName.lastName);
  }, [email, firstName, lastName, parsedName.firstName, parsedName.lastName, userInfo.email]);

  const finishAuth = async ({ linkedAccount = false } = {}) => {
    if (linkedAccount && typeof window !== "undefined") {
      window.sessionStorage.setItem(LINKED_ACCOUNT_LOGIN_KEY, "1");
    }
    await refreshAuth();
    navigate(paths.pmDashboard, { replace: true });
  };

  const handleSetupSubmit = async (event) => {
    event.preventDefault();

    if (!identityToken) {
      setError("Apple setup state is missing. Please sign in again.");
      return;
    }

    const trimmedEmail = email.trim();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedPhone = phone.trim();

    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const result = await loginWithApple({
        ...appleResponse,
        identityToken,
        authorization: appleResponse.authorization || {
          id_token: identityToken,
          code: appleResponse.authorizationCode,
        },
        id_token: appleResponse.id_token || identityToken,
        user: appleResponse.user ?? appleUserId,
        email: trimmedEmail,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        phone: trimmedPhone,
      }, password);

      if (result?.success) {
        await finishAuth();
        return;
      }

      if (result?.requiresLinkApproval) {
        setMode("link");
        setLinkStep("confirm");
        setError(result.error || "An account already exists with this email. Please confirm linking.");
        return;
      }

      if (result?.requiresPassword) {
        setError(result.error || "Apple sign-in needs additional setup.");
        return;
      }

      setError(result?.error || "Apple sign-in failed. Please try again.");
    } catch (err) {
      setError(err?.message || "Apple sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartLink = async () => {
    if (provider === "apple" && (!identityToken || !appleUserId)) {
      setError("Apple linking state is missing. Please sign in again.");
      return;
    }

    if (provider !== "apple" && !providerUserId && !Object.keys(authPayload || {}).length) {
      setError(`${providerName} linking state is missing. Please sign in again.`);
      return;
    }

    const trimmedEmail = email.trim();
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError("Please enter the email address tied to the existing account.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const linkPayload = {
        email: trimmedEmail,
        appleUserId,
        providerUserId,
        identityToken,
        authPayload,
        password: password || undefined,
      };
      const result = provider === "apple"
        ? await linkAppleAccount(linkPayload)
        : await linkSocialAccount(provider, linkPayload);

      if (result?.success) {
        await finishAuth({ linkedAccount: true });
        return;
      }

      if (result?.requiresVerificationCode) {
        setLinkingProcessId(result?.data?.linkingProcessId || result?.linkingProcessId || "");
        setLinkStep("code");
        message.info("We sent a verification code to confirm the link.");
        return;
      }

      if (result?.details?.requiresPassword || result?.requiresPassword) {
        setLinkStep("password");
        message.warning(result?.error || "Enter your password to continue.");
        return;
      }

      setError(result?.error || "Unable to start account linking.");
    } catch (err) {
      setError(err?.message || "Unable to start account linking.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    if (!password.trim()) {
      setError("Enter your password to continue.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const linkPayload = {
        email: email.trim(),
        appleUserId,
        providerUserId,
        identityToken,
        authPayload,
        password,
      };
      const result = provider === "apple"
        ? await linkAppleAccount(linkPayload)
        : await linkSocialAccount(provider, linkPayload);

      if (result?.requiresVerificationCode) {
        setLinkingProcessId(result?.data?.linkingProcessId || result?.linkingProcessId || "");
        setLinkStep("code");
        message.info("We sent a verification code to confirm the link.");
        return;
      }

      if (result?.success) {
        await finishAuth({ linkedAccount: true });
        return;
      }

      setError(result?.error || "Unable to verify your password.");
    } catch (err) {
      setError(err?.message || "Unable to verify your password.");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (event) => {
    event.preventDefault();

    if (verificationCode.trim().length !== 6) {
      setError("Please enter the 6-digit verification code.");
      return;
    }

    if (!linkingProcessId) {
      setError("Missing linking request. Please start the link step again.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const linkPayload = {
        email: email.trim(),
        appleUserId,
        providerUserId,
        identityToken,
        authPayload,
        password: password || undefined,
        verificationCode: verificationCode.trim(),
        linkingProcessId,
      };
      const result = provider === "apple"
        ? await linkAppleAccount(linkPayload)
        : await linkSocialAccount(provider, linkPayload);

      if (result?.success) {
        await finishAuth({ linkedAccount: true });
        return;
      }

      setError(result?.error || "Unable to verify the confirmation code.");
    } catch (err) {
      setError(err?.message || "Unable to verify the confirmation code.");
    } finally {
      setLoading(false);
    }
  };

  const isSetupMode = mode === "setup";
  const isLinkConfirm = mode === "link" && linkStep === "confirm";
  const isLinkPassword = mode === "link" && linkStep === "password";
  const isLinkCode = mode === "link" && linkStep === "code";
  const progress = isSetupMode ? 55 : isLinkConfirm ? 35 : isLinkPassword ? 68 : 92;
  const pageTitle = isSetupMode
    ? `Complete ${providerName} setup`
    : isLinkConfirm
      ? `Link your ${providerName} account`
      : isLinkPassword
        ? "Confirm with password"
        : "Verify the link";
  const pageSubtitle = isSetupMode
    ? `Finish creating your ${providerName} sign-in profile.`
    : isLinkConfirm
      ? `We found an existing Ovacus account for ${email || userInfo.email || "this email"}.`
      : isLinkPassword
        ? `Enter your Ovacus password to approve ${providerName} sign-in.`
        : `Enter the 6-digit code sent to ${email || userInfo.email || "your email"}.`;

  return (
    <AuthShell mode="center">
      <style>{formStyle}</style>
      <div style={{ display: "grid", placeItems: "center", minHeight: "100vh", width: "100%" }}>
        <Card
          variant="borderless"
          style={{
            width: "min(100%, 620px)",
            borderRadius: 28,
            border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
            boxShadow: "0 26px 72px rgba(0, 82, 64, 0.14), inset 0 1px 0 rgba(255,255,255,0.95)",
            background: "linear-gradient(155deg, rgba(255,255,255,0.98) 0%, rgba(246,255,249,0.96) 58%, rgba(255,253,235,0.94) 100%)",
            overflow: "hidden",
          }}
          styles={{ body: { padding: 24 } }}
        >
          <Space direction="vertical" size={18} style={{ width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(paths.simpleLogin)}
                shape="circle"
                style={{
                  color: OLIVEEB_COLORS.oliveebBlue,
                  borderColor: OLIVEEB_COLORS.oliveebBlue,
                  background: OLIVEEB_COLORS.white,
                  boxShadow: "0 10px 22px rgba(0,87,184,0.12)",
                }}
              />
              <div
                style={{
                  padding: "7px 14px",
                  borderRadius: 24,
                  background: OLIVEEB_COLORS.white,
                  border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                  boxShadow: "0 12px 24px rgba(39,174,48,0.14)",
                }}
              >
                <AuthBrand centered size={78} />
              </div>
              <Button
                type="link"
                onClick={() => navigate(paths.simpleLogin)}
                style={{ color: OLIVEEB_COLORS.oliveebBlue, fontWeight: 900, paddingInline: 0 }}
              >
                Sign in
              </Button>
            </div>

            <Progress
              percent={progress}
              showInfo={false}
              strokeColor={OLIVEEB_COLORS.oliveebGreen}
              trailColor={OLIVEEB_COLORS.oliveebGreenSoft}
            />

            <Space direction="vertical" size={6} style={{ width: "100%", textAlign: "center" }}>
              <Text
                style={{
                  color: OLIVEEB_COLORS.oliveebGreen,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  fontSize: 11,
                }}
              >
                {isSetupMode ? `${providerName} setup` : "Account linking"}
              </Text>
              <Title
                level={2}
                style={{
                  color: OLIVEEB_COLORS.oliveebGreen,
                  margin: 0,
                  fontWeight: 900,
                  lineHeight: 1.12,
                }}
              >
                {pageTitle}
              </Title>
              <Paragraph
                style={{
                  margin: 0,
                  color: OLIVEEB_COLORS.oliveebBlue,
                  fontSize: 15,
                  lineHeight: 1.6,
                }}
              >
                {pageSubtitle}
              </Paragraph>
            </Space>

            {error ? (
              <Alert
                type="warning"
                showIcon
                message={isSetupMode ? "Setup needs attention" : "Linking needs attention"}
                description={error}
                style={{ borderRadius: 14, padding: "8px 12px" }}
              />
            ) : null}

            {isSetupMode ? (
              <form className="ovacus-link-form" onSubmit={handleSetupSubmit}>
                <Space direction="vertical" size={14} style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      padding: 16,
                      borderRadius: 18,
                      background: OLIVEEB_COLORS.white,
                      border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                    }}
                  >
                    <div
                      style={{
                        width: 54,
                        height: 54,
                        borderRadius: 999,
                        background: OLIVEEB_COLORS.oliveebGreenSoft,
                        color: OLIVEEB_COLORS.oliveebGreen,
                        display: "grid",
                        placeItems: "center",
                        fontWeight: 900,
                        fontSize: 20,
                      }}
                    >
                      {initialDisplayName?.charAt(0)?.toUpperCase() || providerName.charAt(0)}
                    </div>
                    <div>
                      <Text style={{ color: OLIVEEB_COLORS.oliveebDarkGreen, fontWeight: 900 }}>
                        {initialDisplayName || `${providerName} user`}
                      </Text>
                      <div style={{ color: OLIVEEB_COLORS.textMuted, fontSize: 13 }}>
                        Enter your details to finish setup.
                      </div>
                    </div>
                  </div>

                  <Input
                    prefix={<MailOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    autoComplete="email"
                  />
                  <Input
                    prefix={<UserOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={loading}
                    autoComplete="given-name"
                  />
                  <Input
                    prefix={<UserOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    type="text"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={loading}
                    autoComplete="family-name"
                  />
                  <Input
                    type="tel"
                    placeholder="Phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={loading}
                    autoComplete="tel"
                  />
                  <Input.Password
                    prefix={<LockOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <Input.Password
                    prefix={<LockOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                  />

                  <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
                    <Link to={paths.simpleLogin} style={{ color: OLIVEEB_COLORS.textMuted, fontWeight: 900 }}>
                      Cancel
                    </Link>
                    <Button type="primary" htmlType="submit" loading={loading} style={primaryButtonStyle}>
                      Continue
                    </Button>
                  </Space>
                </Space>
              </form>
            ) : null}

            {isLinkConfirm ? (
              <Space direction="vertical" size={14} style={{ width: "100%" }} className="ovacus-link-form">
                <div
                  style={{
                    padding: 18,
                    borderRadius: 18,
                    background: OLIVEEB_COLORS.white,
                    border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                    boxShadow: "0 14px 28px rgba(39,174,48,0.10)",
                  }}
                >
                  <Space size={12} align="start">
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: 999,
                        display: "grid",
                        placeItems: "center",
                        background: OLIVEEB_COLORS.oliveebBlueSoft,
                        color: OLIVEEB_COLORS.oliveebBlue,
                      }}
                    >
                      <LinkOutlined />
                    </div>
                    <div>
                      <Text style={{ fontWeight: 900, color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                        {email || userInfo.email || "Existing account"}
                      </Text>
                      <Paragraph style={{ color: OLIVEEB_COLORS.textMuted, fontSize: 14, margin: "4px 0 0" }}>
                        {state.message || `We found an existing account that matches this ${providerName} sign-in.`}
                      </Paragraph>
                    </div>
                  </Space>
                </div>

                <Input
                  prefix={<MailOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                  type="email"
                  placeholder="Email tied to the account"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                />
                <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
                  <Button onClick={() => navigate(paths.simpleLogin)} disabled={loading} style={secondaryButtonStyle}>
                    Deny
                  </Button>
                  <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleStartLink} loading={loading} style={primaryButtonStyle}>
                    Accept & Link
                  </Button>
                </Space>
              </Space>
            ) : null}

            {isLinkPassword ? (
              <form className="ovacus-link-form" onSubmit={handlePasswordSubmit}>
                <Space direction="vertical" size={14} style={{ width: "100%" }}>
                  <Input.Password
                    prefix={<LockOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
                    <Button onClick={() => setLinkStep("confirm")} disabled={loading} style={secondaryButtonStyle}>
                      Back
                    </Button>
                    <Button type="primary" htmlType="submit" icon={<SafetyCertificateOutlined />} loading={loading} style={primaryButtonStyle}>
                      Continue
                    </Button>
                  </Space>
                </Space>
              </form>
            ) : null}

            {isLinkCode ? (
              <form className="ovacus-link-form" onSubmit={handleCodeSubmit}>
                <Space direction="vertical" size={14} style={{ width: "100%" }}>
                  <Input
                    prefix={<SafetyCertificateOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="123456"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/[^\d]/g, ""))}
                    disabled={loading}
                    autoComplete="one-time-code"
                  />
                  <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
                    <Button onClick={() => setLinkStep("password")} disabled={loading} style={secondaryButtonStyle}>
                      Back
                    </Button>
                    <Button type="primary" htmlType="submit" icon={<CheckCircleOutlined />} loading={loading} style={primaryButtonStyle}>
                      Confirm link
                    </Button>
                  </Space>
                </Space>
              </form>
            ) : null}
          </Space>
        </Card>
      </div>
    </AuthShell>
  );
};

export default AuthAppleSetup;
